import React, { useEffect, useState } from "react";
import {
    Heading,
    Flex,
    Text,
    FormLabel,
    Input,
    Textarea,
    FormControl,
    InputGroup,
    InputRightElement,
    Button,
    Container,
    Spinner, useToast
} from "@chakra-ui/react";
import { useHistory } from "react-router-dom";
import MultiSelect from "react-multi-select-component";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm, Controller } from "react-hook-form";

import Layout from "@pages/Layout";
import { Separator, ErrorBox  } from "@components";
import { IconTriangle, IconLocation, IconCurrency } from "@assets/Icons";
import { addAnnonce } from "@lib/datahandlers/company";
import { annonceSchema } from "@lib/validationData"
import { dataList } from "@lib/datahandlers/global";
import { t } from "@lang/i18n";

// Import React Script Libraray to load Google object
import Script from 'react-load-script';
import { getPredictions } from '@lib/autocomplete';
import { google_api_key } from "@config";

const multiselect_strings = {
  allItemsAreSelected: t('multiselect.allItemsAreSelected'),
  clearSearch: t('multiselect.clearSearch'),
  noOptions: t('multiselect.noOptions'),
  search: t('multiselect.search'),
  selectAll: t('multiselect.selectAll'),
  selectSomeItems: t('multiselect.selectSomeItems'),
};

const AddAnnonce = () => {
    const initForm = {
        longitude: "",
        latitude: "",
        city: "", 
        department: "", 
        region: "",
    };
    const initDataList = {
        fonctions: [],
        contracts: [],
        experiences: [],
    };

    const [form, setForm] = useState(initForm);
    const [selectData, setSelectData] = useState(initDataList);
    const [isLoaded, setIsLoaded] = useState(false);

    const history = useHistory();
    const toast = useToast();
    const { register, handleSubmit, control, setValue, formState: { errors } } = useForm({
      resolver: yupResolver(annonceSchema)
    });

    // filter  multi select
    const filterOptions = (options, value) => {
      if (!value) return options;
      
      const normalized_value = value.normalize('NFD').replace(/[\u0300-\u036f]/g, "").toLowerCase();

      const result = options.filter((el, idx) => {
      const normalized_item = el.label.normalize('NFD').replace(/[\u0300-\u036f]/g, "").toLowerCase();
      if (normalized_item.includes(normalized_value)) return el
      });

      return result
    };

    // add an annonce
    const onAdd = async (data) => {
      const { fonctions, experience, contracts, nbr_positions, address, salary_min, salary_max, description } = data;
      const { region, department, city, longitude, latitude } = form;
      
      let new_data = {
        fonctions: fonctions.map(el => el.value).join(','),
        contracts: contracts.map(el => el.value).join(','),
        experiences: experience.map(el => el.value).join(','),
        nbr_positions, address, salary_min, salary_max, description,
        region, department, city, longitude, latitude
      };

      setIsLoaded(true)

      const result = await addAnnonce(new_data);

      if (result.success) {
        setIsLoaded(false);
        toast({ description: t("messages.success.annonce_add") , status: "success", position: "bottom-right", variant: "left-accent", isClosable: true });
        history.push('/company/annonce')
        return;
      }
      setIsLoaded(false);
      toast({ description: t("messages.error.annonce_add") , status: "error", position: "bottom-right", variant: "left-accent", isClosable: true });
    };
     // load data list
    const getDataList = async () => {
      const data = await dataList();

      setSelectData({
        ...selectData,
        fonctions: data.fonctions.map(el => ({ value: el.id, label: el.name })),
        contracts: data.contracts.map(el => ({ value: el.id, label: el.name })),
        experiences: data.experience_intervals.map(el => ({ value: el.id, label: el.name })),
      });
    };

    /* get / set localization */
    const onPlaceSelected = (params) => {
      setForm(prevState => {
        return { ...prevState, ...params }
      });
    };

    /* load data list */
    useEffect(() => {
      getDataList();
    }, []);

    // set default values to multi select
    useEffect(() => {
      setValue('fonctions', []);
      setValue('experience', []);
      setValue('contracts', []);
    }, []);

  return (
    <Layout menuType="company" isNavBarVisible isFooterVisible>
      <Container maxW="1024px" bg="white" minHeight='calc(100vh - 100px)' py="36px" px={["16px","","","100px"]} borderRadius="4px">
        <Flex flexDir="column">

        <Heading as="h1" fontSize="30px" color="black" textAlign="center">
            {t("company.annonce.add_annonce.title")}
        </Heading>

        <Separator />

        {/* Add Annonces Button */}
        <Text fontWeight="700" fontSize="20px" textAlign="left" mb="23px">
          {t("company.annonce.add_annonce.step_1")}
        </Text>

        <Heading as="h1" fontWeight="600" fontSize="16px" textAlign="left" mb="27px">
          {t("company.annonce.add_annonce.description_1")}
        </Heading>

        <form onSubmit={handleSubmit(onAdd)}>
          <Flex flexDir="row" flexWrap="wrap" justifyContent={{base: "center", md: "space-around", lg: "space-between"}}>
              <FormControl id="fonctions" mb="1.625rem" maxW="385px">
                <FormLabel> { t('fields.company.fonctions') } </FormLabel>
                  <Controller name="fonctions" render={({ field }) => (
                      <MultiSelect
                          {...field}
                          options={selectData.fonctions}
                          onChange={(val) => setValue('fonctions', val)}
                          ArrowRenderer={() => (<IconTriangle iconType="company" position="down" />)}
                          filterOptions={filterOptions}
                          valueRenderer={(val) => !val.length ? t("fields.company.fonctions") : null}
                          className={("className", errors.fonctions ? "multi-select company register error" : "multi-select company register")}
                          overrideStrings={multiselect_strings}
                      />
                    )}
                    control={control}
                    defaultValue=""
                  />
                {errors.fonctions && <ErrorBox error={errors.fonctions.message} />}
              </FormControl>

              <FormControl id="nbr_positions" mb="1.625rem" maxW="385px">
                <FormLabel> { t('fields.company.nbr_positions') } </FormLabel>
                <Input
                  type="number"
                  min="1"
                  placeholder={ t('fields.company.nbr_positions') }
                  borderColor="gray.borders"
                  borderRadius="4px"
                  bg="white"
                  maxW="385px"
                  h="40px"
                  errorBorderColor="error"
                  isInvalid={errors.nbr_positions ? true : false}
                  focusBorderColor={errors.nbr_positions ? "error" : "green.light"}
                  {...register("nbr_positions")}
                />
                {errors.nbr_positions && <ErrorBox error={errors.nbr_positions.message} width="385px" />}
              </FormControl>

              <FormControl id="experiences" mb="1.625rem" maxW="385px">
                <FormLabel> { t('fields.company.experience') } </FormLabel>
                    <Controller name="experience" render={({ field }) => (
                        <MultiSelect
                            {...field}
                            options={selectData.experiences}
                            onChange={(val) => setValue('experience', val)}
                            ArrowRenderer={() => (<IconTriangle iconType="company" position="down" />)}
                            filterOptions={filterOptions}
                            valueRenderer={(val) => !val.length ? t("fields.company.experience") : null}
                            className={("className", errors.experience ? "multi-select company register error" : "multi-select company register")}
                            overrideStrings={multiselect_strings}
                        />
                      )}
                      control={control}
                      defaultValue=""
                    />
                  {errors.experience && <ErrorBox error={errors.experience.message} width="385px" />}
              </FormControl>

              <Script url={`https://maps.googleapis.com/maps/api/js?key=${google_api_key}&libraries=places`}
                  onLoad={() => getPredictions({ inputId:'autocomplete', callback: onPlaceSelected })}
                />
              <FormControl id="address" mb="1.625rem" maxW="385px" pos="relative">
                <FormLabel> { t('fields.company.work_location') } </FormLabel>
                <InputGroup>
                  <Input
                    type="text"
                    placeholder={ t('fields.company.work_location') }
                    _placeholder={{color:'#AAA'}}
                    borderColor="gray.borders"
                    borderRadius="3px"
                    bg="white"
                    maxW="385px"
                    h="40px"
                    focusBorderColor={errors.address ? "error" : "green.light"}
                    autoComplete="off"
                    errorBorderColor="error"
                    isInvalid={errors.address ? true : false}
                    id="autocomplete"
                    {...register("address")}
                  />

                  <InputRightElement
                    children={<IconLocation iconType="company" />}
                    top="-1px"
                  />
                </InputGroup>

                {errors.address && <ErrorBox error={errors.address.message} width="385px" />}
              </FormControl>

              <FormControl id="contracts" mb="1.625rem" maxW="385px">
                <FormLabel> { t('fields.company.contracts') } </FormLabel>
                  <Controller name="contracts" render={({ field }) => (
                      <MultiSelect
                          {...field}
                          options={selectData.contracts}
                          onChange={(val) => setValue('contracts', val)}
                          ArrowRenderer={() => (<IconTriangle iconType="company" position="down" />)}
                          filterOptions={filterOptions}
                          valueRenderer={(val) => !val.length ? t("fields.company.contracts") : null}
                          className={("className", errors.contracts ? "multi-select company register error" : "multi-select company register")}
                          overrideStrings={multiselect_strings}
                      />
                    )}
                    control={control}
                    defaultValue=""
                  />
                  {errors.contracts && <ErrorBox error={errors.contracts.message} width="385px" />}
              </FormControl>

              <FormControl id="salary_min" mb="1.625rem" maxW="385px">
                <FormLabel> { t('fields.company.salary_min') } </FormLabel>
                <InputGroup>
                    <Input
                      type="number"
                      min="0"
                      placeholder={ t('fields.company.salary_min') }
                      borderColor="gray.borders"
                      borderRadius="4px"
                      bg="white"
                      maxW="385px"
                      h="40px"
                      focusBorderColor={errors.salary_min ? "error" : "green.light"}
                      errorBorderColor="error"
                      isInvalid={errors.salary_min ? true : false}
                      {...register("salary_min")}
                    />
                    <InputRightElement
                      children={<IconCurrency iconType="company" />}
                      top="-1px"
                    />
                </InputGroup>
                {errors.salary_min && <ErrorBox error={errors.salary_min.message} width="385px" />}
              </FormControl>

              <FormControl id="salary_max" mb="1.625rem" maxW="385px">
                <FormLabel> { t('fields.company.salary_max') } </FormLabel>
                <InputGroup>
                    <Input
                      type="number"
                      min="0"
                      placeholder={ t('fields.company.salary_max') }
                      borderColor="gray.borders"
                      borderRadius="4px"
                      bg="white"
                      maxW="385px"
                      h="40px"
                      focusBorderColor={errors.salary_max ? "error" : "green.light"}
                      errorBorderColor="error"
                      isInvalid={errors.salary_max ? true : false}
                      {...register("salary_max")}
                    />
                    <InputRightElement
                      children={<IconCurrency iconType="company" />}
                      top="-1px"
                    />
                </InputGroup>
                {errors.salary_max && <ErrorBox error={errors.salary_max.message} width="385px" />}
              </FormControl>

              <FormControl id="address" mb="1.625rem" maxW={{base: "85vw", md: "50vw", lg: "100vw"}}>
                <FormLabel> { t('company.annonce.add_annonce.desc_annonce') } </FormLabel>
                <Textarea
                  placeholder={ t('company.annonce.add_annonce.desc_annonce') }
                  maxW={{base: "85vw", md: "50vw", lg: "100vw"}} mx={{base: "auto", lg: "0"}} h="145px"
                  focusBorderColor={errors.description ? "error" : "green.light"}
                  borderColor="gray.borders"
                  borderRadius="4px"
                  errorBorderColor="error"
                  isInvalid={errors.description ? true : false}
                  {...register("description")}
                />
                {errors.description && <ErrorBox error={errors.description.message} width={{base: "85vw", md: "50vw", lg: "100vw"}} />}
              </FormControl>
            </Flex>
            
            <Button type="submit"
              w={["60vw", "344px", "", ""]} h="40px" p="0 16px" m="26px auto 12px auto" borderRadius="24px" border="2px solid" 
              borderColor="green.main" bg="green.main" color="white" display="flex"
              _hover={{ bg: "green.light", borderColor: "green.light" }}>
              {isLoaded? <Spinner size="md" /> : t('global.next')}
            </Button>
          </form>

        </Flex>
      </Container>
    </Layout>
  );
};

export default AddAnnonce;
